import type { RuleSet } from '../../rules/schema';
import { invalidateDerived } from '../derivedRegistry';
import { MILLI, toMilli } from '../fixed';
import { isMilitary } from '../occupancy';
import { canAfford, spend } from '../stock';
import type { PlayerId, ResourceId, SimState, SystemFn } from '../types';

/** How many military units `owner` has (stored ones too). */
function armySize(state: SimState, rules: RuleSet, owner: PlayerId): number {
  let n = 0;
  for (const u of Object.values(state.units)) {
    if (u.owner === owner && isMilitary(rules, u.type)) n++;
  }
  return n;
}

/**
 * What an army of `size` multiplies upkeep by: 1 + `pacing.armyUpkeep` per
 * military unit, so a big army costs more per soldier.
 */
function upkeepScale(rules: RuleSet, size: number): number {
  return 1 + rules.pacing.armyUpkeep * size;
}

/**
 * Resources per second `owner`'s units cost: their types' rules `upkeep`,
 * summed, times `upkeepScale` for the player's army size.
 */
function upkeepOf(state: SimState, rules: RuleSet, owner: PlayerId): Record<ResourceId, number> {
  const total: Record<ResourceId, number> = {};
  for (const u of Object.values(state.units)) {
    if (u.owner !== owner) continue;
    for (const [res, rate] of Object.entries(rules.units[u.type]?.upkeep ?? {})) {
      total[res] = (total[res] ?? 0) + rate;
    }
  }
  const scale = upkeepScale(rules, armySize(state, rules, owner));
  for (const res of Object.keys(total)) total[res] = (total[res] ?? 0) * scale;
  return total;
}

/** Slack for float drift in `state.elapsed` when comparing against the grace period. */
const GRACE_EPSILON = 1e-6;

/**
 * Whether `owner`'s army is losing hp to hunger: they have been unable to pay
 * upkeep in full since `starvingSince` for at least `pacing.starvationGrace`
 * seconds.
 */
function isStarving(state: SimState, rules: RuleSet, owner: PlayerId): boolean {
  const since = state.players[owner]?.starvingSince;
  return (
    since !== undefined && state.elapsed - since >= rules.pacing.starvationGrace - GRACE_EPSILON
  );
}

/**
 * Units cost their upkeep every tick, paid from their owner's cities like any
 * cost (the city holding the most first, see stock.ts `spend`). A player who
 * can't pay it in full pays what they have and is marked `starvingSince` (the
 * elapsed time it began); once that has lasted `pacing.starvationGrace`
 * seconds, each of their military units (stored ones too) loses
 * `pacing.attrition` hp/s, and one that runs out dies. Paying in full (or
 * owing nothing) clears the mark. Runs after the economy (this tick's
 * deposits count) and before combat.
 */
const upkeep: SystemFn = (state, ctx, _inputs, dt) => {
  const { rules } = ctx;
  const attrition = toMilli(rules.pacing.attrition * dt);
  for (const owner of Object.keys(state.players).sort()) {
    const player = state.players[owner];
    if (!player || player.eliminated) continue;
    // In whole thousandths, like the stock it's paid from: what the rate has
    // owed by the end of this tick less what it owed by its start, so the
    // rounding doesn't add up (a small rate is charged now and then, never
    // rounded away or up every tick).
    const t = state.tick;
    const cost: Record<ResourceId, number> = {};
    for (const [res, rate] of Object.entries(upkeepOf(state, rules, owner))) {
      const perTick = rate * dt * MILLI;
      const amount = Math.floor(perTick * (t + 1)) - Math.floor(perTick * t);
      if (amount > 0) cost[res] = amount;
    }
    if (Object.keys(cost).length === 0 || canAfford(state, owner, cost)) {
      spend(state, owner, cost);
      delete player.starvingSince;
      continue;
    }
    spend(state, owner, cost);
    player.starvingSince ??= state.elapsed;
    if (attrition <= 0 || !isStarving(state, rules, owner)) continue;
    for (const u of Object.values(state.units)) {
      if (u.owner !== owner || !isMilitary(rules, u.type)) continue;
      u.hp -= attrition;
      if (u.hp > 0) continue;
      delete state.units[u.id];
      invalidateDerived(state);
    }
  }
};

export { armySize, isStarving, upkeep, upkeepOf, upkeepScale };
